import { useQuery } from '@apollo/client/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    calculateBMI,
    mapPerformanceResults,
    mapProfile,
    mapReportHeaderData,
    mapTraineeReportData,
} from '../../../Reports/shared/utils';

import {
    AssessmentResults,
    FetchTraineePerformanceResults,
    FetchUserDocument,
    IncompleteModules,
} from '../TraineeAssessmentResults.graphql';
import useAgeCalculation from '@/utils/useAgeCalculation';
import useDateFormats from '@/utils/useDateFormats';

interface UseOverallTraineeReportDataProps {
    assessmentRunId: string;
    userId: string;
    programId?: string;
    programName?: string;
    startDate?: Date;
    endDate?: Date;
}

const useOverallTraineeReportData = ({
    assessmentRunId,
    userId,
    programId,
    programName,
    startDate,
    endDate,
}: UseOverallTraineeReportDataProps) => {
    const { t } = useTranslation('overallTraineeReport');
    const { formatDate } = useDateFormats();
    const { formatAge } = useAgeCalculation();

    const queryFilter = useMemo(
        () => ({
            assessmentRunIds: assessmentRunId ? [assessmentRunId] : [],
            userIds: [userId],
        }),
        [assessmentRunId, userId]
    );

    const {
        loading: loadingCompleted,
        data: completedData,
        error: completedError,
    } = useQuery(AssessmentResults, {
        fetchPolicy: 'cache-and-network',
        variables: { filter: queryFilter },
        skip: !assessmentRunId || !userId,
    });

    const {
        loading: loadingPerformance,
        data: performanceData,
        error: performanceError,
    } = useQuery(FetchTraineePerformanceResults, {
        fetchPolicy: 'cache-and-network',
        variables: { filter: queryFilter },
        skip: !assessmentRunId || !userId,
    });

    const {
        loading: loadingIncomplete,
        data: incompleteData,
        error: incompleteError,
    } = useQuery(IncompleteModules, {
        fetchPolicy: 'cache-and-network',
        variables: {
            programId: programId || '',
            userId: userId,
            assessmentRunId: assessmentRunId || '',
        },
        skip: !programId || !userId || !assessmentRunId,
    });

    const {
        loading: loadingUser,
        data: userData,
        error: userError,
    } = useQuery(FetchUserDocument, {
        fetchPolicy: 'cache-and-network',
        variables: { userId: userId },
        skip: !userId,
    });

    const loading = loadingCompleted || loadingIncomplete || loadingPerformance || loadingUser;
    const error = completedError || incompleteError || performanceError || userError;

    const assessmentResults = useMemo(() => completedData?.assessmentResults.items ?? [], [completedData]);
    const teamResilienceItems = completedData?.assessmentResults.teamResilienceItems ?? [];
    const incompleteItems = incompleteData?.incompleteModules ?? [];
    const hasAnyAssessmentItems = assessmentResults.length > 0 || incompleteItems.length > 0;

    const profile = mapProfile(t, userData?.user?.__typename === 'TenantUser' ? userData.user : null, formatAge);
    const reportHeaderData = mapReportHeaderData(
        t,
        formatDate,
        programName ?? '',
        startDate ?? new Date(),
        endDate ?? new Date(),
        assessmentResults,
        incompleteItems
    );
    const traineeReportData = mapTraineeReportData(t, assessmentResults, teamResilienceItems, incompleteItems);
    const { trainerNotes, physiologicalFitness, psychologicalFitness, cognitiveFitness } = traineeReportData;
    const performance = mapPerformanceResults(t, formatDate, performanceData?.performanceResults?.items ?? []);

    const weight = useMemo(() => {
        const inbodyResult = assessmentResults.find(r => r.result.__typename === 'InbodyCompositionResult')?.result;
        if (!inbodyResult || inbodyResult.__typename !== 'InbodyCompositionResult' || !inbodyResult.weightKg) {
            return null;
        }
        return inbodyResult.weightKg;
    }, [assessmentResults]);

    profile.weightKg = weight?.toFixed(2) ?? t('traineeReports:na');
    const heightCm = Number(profile.heightCm);
    const hasValidHeight = Number.isFinite(heightCm) && heightCm > 0;
    profile.bmi = weight && hasValidHeight ? calculateBMI(weight, heightCm / 100).toFixed(2) : t('traineeReports:na');

    return {
        loading,
        error,
        hasAnyAssessmentItems,
        profile,
        reportHeaderData,
        trainerNotes: trainerNotes || '',
        physiologicalFitness,
        psychologicalFitness,
        cognitiveFitness,
        performance,
    };
};

export default useOverallTraineeReportData;
